import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LockSimple, DownloadSimple, ArrowLeft, CheckCircle, ArrowCounterClockwise } from "@phosphor-icons/react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import ReportPreview from "./ReportPreview";
import SimpleReportView from "./SimpleReportView";
import StatusBanner, { StatusPill } from "./StatusBanner";
import { apiError, btnGhost, btnPrimary, downloadFile, fmtDateTimeWIB, fmtIDR, inp } from "./util";

const TIMING = { ON_TIME: "Tepat waktu", LATE: "Terlambat", NOT_REQUIRED: "Tidak wajib" };
const fmtVal = (v) => (Array.isArray(v) ? v.join(", ") || "—" : typeof v === "number" ? v.toLocaleString("id-ID") : v || "—");

function RevisionHistory({ versions, events }) {
    return (
        <div className="space-y-3" data-testid="revision-history">
            <h3 className="text-[10px] uppercase tracking-[0.25em] text-neutral-400">Riwayat revisi</h3>
            {versions.map((v) => (
                <div key={v.version} className="border border-mist bg-white rounded-sm p-3 text-xs" data-testid={`revision-${v.version}`}>
                    <div className="text-ink">Revisi {v.version} · {v.kind === "EDIT" ? "Koreksi" : "Pengiriman"} oleh {v.submitted_by_name} · {fmtDateTimeWIB(v.submitted_at)}</div>
                    {v.changes?.length > 0 && (
                        <ul className="mt-1.5 space-y-0.5 text-neutral-600">
                            {v.changes.map((c, i) => (
                                <li key={i}><span className="font-mono">{c.field}</span>: {fmtVal(c.old)} → <b className="text-ink">{fmtVal(c.new)}</b></li>
                            ))}
                        </ul>
                    )}
                </div>
            ))}
            {(events || []).filter((e) => e.type === "APPROVED" || e.type === "REOPENED").map((e, i) => (
                <div key={i} className="text-xs text-neutral-600" data-testid={`event-${e.type.toLowerCase()}-${i}`}>
                    {e.type === "APPROVED" ? "Disetujui" : "Dibuka ulang"} oleh {e.actor_name} · {fmtDateTimeWIB(e.at)} (revisi {e.revision}){e.reason ? ` — “${e.reason}”` : ""}
                </div>
            ))}
        </div>
    );
}

function OwnerActions({ r, onChanged }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const act = async (fn, msg) => {
        setBusy(true);
        try {
            await fn();
            toast.success(msg);
            onChanged();
        } catch (e) {
            toast.error(await apiError(e));
        } finally {
            setBusy(false);
        }
    };
    if (r.status === "SUBMITTED") {
        return (
            <button type="button" disabled={busy} className={btnPrimary} data-testid="approve-report-btn"
                onClick={() => window.confirm("Setujui dan kunci laporan ini?") && act(() => api.post(`/reporting/reports/${r.id}/approve`), "Laporan disetujui")}>
                <CheckCircle size={14} /> Setujui
            </button>
        );
    }
    if (r.status === "APPROVED") {
        return (
            <div className="flex flex-wrap gap-2 items-center">
                <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Alasan membuka ulang (wajib)" className={`${inp} w-64`} data-testid="reopen-reason-input" />
                <button type="button" disabled={busy || !reason.trim()} className={btnGhost} data-testid="reopen-report-btn"
                    onClick={() => act(() => api.post(`/reporting/reports/${r.id}/reopen`, { reason }), "Laporan dibuka ulang")}>
                    <ArrowCounterClockwise size={14} /> Buka ulang
                </button>
            </div>
        );
    }
    return null;
}

function ReportDetail({ id, onBack }) {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [tick, setTick] = useState(0);
    useEffect(() => {
        api.get(`/reporting/reports/${id}`).then(({ data }) => setData(data)).catch(async (e) => toast.error(await apiError(e)));
    }, [id, tick]);
    if (!data) return <div className="text-sm text-neutral-400">Memuat…</div>;
    const r = data.report;
    const isForm = r.method === "SIMPLE_FORM";
    const current = (data.attachments || []).filter((a) => (r.attachment_ids || []).includes(a.id));
    const dl = async () => {
        try {
            await downloadFile(`/reporting/reports/${id}/file`, {}, data.file?.original_filename || "report.xlsx");
        } catch (e) {
            toast.error(await apiError(e));
        }
    };
    return (
        <div className="space-y-6" data-testid="report-detail">
            <button type="button" onClick={onBack} className="text-xs uppercase tracking-widest text-neutral-500 hover:text-ink inline-flex items-center gap-1" data-testid="report-detail-back">
                <ArrowLeft size={14} /> Semua laporan
            </button>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="font-serif text-2xl">{r.reporting_date} · {r.admin_name}</h2>
                    <div className="text-xs text-neutral-500 mt-1">{isForm ? "Form harian" : `Excel ${r.template_version}`}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {!isForm && (
                        <>
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-neutral-100 text-neutral-600 text-[10px] uppercase tracking-widest" data-testid="report-locked-badge"><LockSimple size={12} /> Terkunci</span>
                            <button type="button" onClick={dl} className={btnGhost} data-testid="download-original-btn"><DownloadSimple size={14} /> File asli</button>
                        </>
                    )}
                    {isForm && user?.role === "owner" && <OwnerActions r={r} onChanged={() => setTick((t) => t + 1)} />}
                </div>
            </div>
            {isForm ? <StatusBanner report={r} /> : (
                <div className="text-xs text-neutral-500">Dikirim {fmtDateTimeWIB(r.submitted_at)} · {TIMING[r.submission_timing] || r.submission_timing}</div>
            )}
            {isForm ? (
                <SimpleReportView figures={r.figures} handover={r.handover} totals={r.totals} attachments={current} />
            ) : (
                <ReportPreview preview={data.preview} />
            )}
            {isForm && <RevisionHistory versions={data.versions} events={r.events} />}
        </div>
    );
}

function ApproveAll({ brand, date, eligible, onDone }) {
    const [busy, setBusy] = useState(false);
    const run = async () => {
        if (!window.confirm(`Setujui dan kunci ${eligible} laporan terkirim untuk ${date}?`)) return;
        setBusy(true);
        try {
            const { data } = await api.post("/reporting/reports/approve-all", { brand, reporting_date: date });
            toast.success(`${data.approved} laporan disetujui`);
            onDone();
        } catch (e) {
            toast.error(await apiError(e));
        } finally {
            setBusy(false);
        }
    };
    return (
        <button type="button" onClick={run} disabled={busy || !date || !eligible} className={btnPrimary} data-testid="approve-all-btn">
            <CheckCircle size={14} /> Setujui semua ({eligible})
        </button>
    );
}

export default function ReportsList({ brand, refreshKey }) {
    const { user } = useAuth();
    const isOwner = user?.role === "owner";
    const [items, setItems] = useState(null);
    const [openId, setOpenId] = useState(null);
    const [date, setDate] = useState("");
    const [tick, setTick] = useState(0);
    useEffect(() => {
        setItems(null);
        api.get("/reporting/reports", { params: { brand, reporting_date: date || undefined } })
            .then(({ data }) => setItems(data))
            .catch(async (e) => { toast.error(await apiError(e)); setItems([]); });
    }, [brand, refreshKey, date, tick, openId]);
    if (openId) return <ReportDetail id={openId} onBack={() => setOpenId(null)} />;
    const eligible = (items || []).filter((r) => r.method === "SIMPLE_FORM" && r.status === "SUBMITTED").length;
    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
                <label className="text-xs text-neutral-500">
                    Tanggal laporan
                    <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={`${inp} mt-1 w-48`} data-testid="reports-date-filter" />
                </label>
                {isOwner && date && <ApproveAll brand={brand} date={date} eligible={eligible} onDone={() => setTick((t) => t + 1)} />}
                {isOwner && !date && <span className="text-xs text-neutral-400">Pilih tanggal untuk memakai Setujui semua.</span>}
            </div>
            {items === null && <div className="text-sm text-neutral-400">Memuat…</div>}
            {items && !items.length && <div className="text-sm text-neutral-400 border border-dashed border-mist rounded-sm p-6" data-testid="reports-empty">Belum ada laporan.</div>}
            {items && items.length > 0 && (
                <div className="overflow-x-auto border border-mist rounded-sm bg-white" data-testid="reports-list">
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-50 text-neutral-500 text-xs">
                            <tr>
                                {["Tanggal", "Admin", "Metode", "Status", "Ketepatan", "Nilai Penjualan", "Pembayaran", "Ongkir", ""].map((h) => (
                                    <th key={h} className="text-left font-normal px-3 py-2 whitespace-nowrap">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((r) => (
                                <tr key={r.id} className="border-t border-mist hover:bg-ivory/60 cursor-pointer" onClick={() => setOpenId(r.id)} data-testid={`report-row-${r.id}`}>
                                    <td className="px-3 py-2.5 whitespace-nowrap">{r.reporting_date}</td>
                                    <td className="px-3 py-2.5">{r.admin_name}</td>
                                    <td className="px-3 py-2.5 text-xs text-neutral-600" data-testid={`report-method-${r.id}`}>{r.method === "SIMPLE_FORM" ? "Form harian" : "Excel"}</td>
                                    <td className="px-3 py-2.5" data-testid={`report-status-${r.id}`}>
                                        {r.method === "SIMPLE_FORM" ? <StatusPill status={r.status} /> : <span className="inline-flex items-center gap-1 text-xs"><LockSimple size={12} /> Terkunci</span>}
                                    </td>
                                    <td className={`px-3 py-2.5 text-xs ${r.submission_timing === "LATE" ? "text-amber-700" : "text-neutral-600"}`}>{TIMING[r.submission_timing] || "—"}</td>
                                    <td className="px-3 py-2.5 tabular-nums">{fmtIDR(r.totals?.total_sales_idr)}</td>
                                    <td className="px-3 py-2.5 tabular-nums">{fmtIDR(r.totals?.total_payment_idr)}</td>
                                    <td className="px-3 py-2.5 tabular-nums">{fmtIDR(r.totals?.total_shipping_idr)}</td>
                                    <td className="px-3 py-2.5 text-xs text-sage-dark">Lihat</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
